import { useQueryClient } from "@tanstack/react-query";
import React, { Children, useEffect, useReducer } from "react";
import { withTranslation } from "react-i18next";

import FieldRequestPreviewHeader from "@krea/common/shared-components/field-request/FieldRequestPreviewHeader";
import FieldRequestSaved from "@krea/common/shared-components/field-request/FieldRequestSaved";
import { Error } from "@krea/common/shared-components/fields/error";
import Fieldset from "@krea/common/shared-components/fields/fieldset";
import { Select } from "@krea/common/shared-components/fields/select";
import { REASONS } from "@krea/common/shared-components/loan-application-form/fields/reason";
import { usePostFieldRequest } from "@krea/common/store/field-requests/hooks/mutations";
import { useGetOneFieldRequest } from "@krea/common/store/field-requests/hooks/queries";

import { useGetCachedCurrentLoanApplication } from "../../../store/current-loan-application/hooks";
import {
  CURRENT_LOAN_APPLICATION_STORE_KEY,
  FIELD_REQUESTS_STORE_KEY,
} from "../../../store/queryClient";

const ReasonForm = ({ fieldRequestID, isPreviewMode, t, withInputLabel }) => {
  const { data: { displayName, submittedData } = {} } =
    useGetOneFieldRequest(fieldRequestID);
  const {
    data: { id: loanApplicationID },
  } = useGetCachedCurrentLoanApplication();
  const { mutate: postFieldRequest, error } =
    usePostFieldRequest(fieldRequestID);
  const queryClient = useQueryClient();

  const supportedReasons = REASONS;
  const errors = error?.data?.errors || [];

  function reducer(state, action) {
    switch (action.type) {
      case "reason":
        return { ...state, reason: action.payload };
      case "touched":
        return { ...state, touched: action.payload };
      default:
        throw new Error();
    }
  }

  const [state, setState] = useReducer(reducer, {
    reason: submittedData?.reason || "",
    touched: false,
  });

  const submittedReasonName = supportedReasons.find(
    (item) => item.value === state.reason,
  )?.name;

  const setReasonState = ({ target }) => {
    const { value } = target;
    setState({ type: "reason", payload: value });
    state.reason !== value && setState({ type: "touched", payload: true });
  };

  const setFocus = ({ target }) => {
    target.focus();
  };

  const onBlur = (state) => {
    const { reason, touched } = state;

    if (touched) {
      postFieldRequest(
        { reason },
        {
          onSuccess: () => {
            queryClient.invalidateQueries([
              CURRENT_LOAN_APPLICATION_STORE_KEY,
              loanApplicationID,
            ]);
          },
        },
      );
      setState({ type: "touched", payload: false });
    }
  };

  useEffect(() => {
    onBlur(state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <>
      {isPreviewMode ? (
        <FieldRequestPreviewHeader
          label={t("applicationForm.commons.reasonLabel")}
          value={
            <>
              <option value="" disabled hidden>
                Välj anledning
              </option>
              {submittedReasonName && <div>{submittedReasonName}</div>}
            </>
          }
        />
      ) : (
        <form
          className="input-form mb-5"
          onBlur={() => onBlur(state)}
          onSubmit={(e) => e.preventDefault()}
        >
          <Fieldset>
            <Select
              label={withInputLabel && displayName}
              onClick={setFocus}
              value={state.reason}
              onChange={setReasonState}
            >
              {Children.toArray(
                supportedReasons.map((item) => (
                  <option value={item.value}>{item.name}</option>
                )),
              )}
            </Select>
          </Fieldset>
          <FieldRequestSaved fieldRequestID={fieldRequestID} />

          {errors.map((error, index) => (
            <Error className="mt-3" key={index}>
              {error.message}
            </Error>
          ))}
        </form>
      )}
    </>
  );
};

export default withTranslation()(ReasonForm);
